/**
 * Hook for automatic chat creation when new matches are detected
 */

import { useCreateChat, useNewMatches } from "@features/messaging/queries";
import type { Match } from "@features/messaging/types";
import logger from "@logger";
import { matchesQueryKeys } from "@shared/queryKeys";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

/**
 * Hook for automatic chat creation
 */
export function useAutoChatCreation(userId: string) {
  const queryClient = useQueryClient();
  const { data: newMatchesData } = useNewMatches(userId);
  const createChatMutation = useCreateChat();

  const newMatches = newMatchesData ?? [];

  useEffect(() => {
    if (!userId || newMatches.length === 0) {
      return;
    }

    logger.info("Checking for automatic chat creation", {
      userId,
      newMatchesCount: newMatches.length,
    });

    // Process each new match
    newMatches.forEach((match) => {
      try {
        const shouldCreateChat = shouldAutoCreateChat(match, userId, queryClient);

        if (shouldCreateChat) {
          logger.info("Auto-creating chat for match", {
            userId,
            matchId: match.user.uid,
          });

          // Create chat
          createChatMutation.mutate(
            {
              userId1: userId,
              userId2: match.user.uid,
            },
            {
              onSuccess: (chatId) => {
                logger.info("Auto-created chat successfully", {
                  chatId,
                  matchId: match.user.uid,
                });
              },
              onError: (error) => {
                logger.error("Error auto-creating chat:", error);
              },
            }
          );
        }
      } catch (error) {
        logger.error("Error processing auto-chat creation:", error);
      }
    });
  }, [userId, newMatches, createChatMutation, queryClient]);

  return {
    isCreatingChat: createChatMutation.isPending,
    createChatError: createChatMutation.error,
  };
}

/**
 * Determine if a chat should be created automatically for a match.
 * Uses React Query cache only; chat_partner_uids (via cached matches) is the source of truth.
 * When cache is empty we skip auto-create this tick to avoid calling getAllMatches (next refetch will populate cache).
 */
function shouldAutoCreateChat(match: Match, _userId: string, queryClient: ReturnType<typeof useQueryClient>): boolean {
  if (match.chat) {
    return false;
  }

  const cached = queryClient.getQueryData<Match[]>(matchesQueryKeys.allMatches(_userId));
  if (!cached) {
    return false;
  }

  const existingMatch = cached.find((m) => m.user.uid === match.user.uid);
  if (existingMatch?.chat) {
    logger.info("Chat already exists for match", {
      matchId: match.user.uid,
      chatId: existingMatch.chat.id,
    });
    return false;
  }

  return true;
}
